import React from "react";
import { Link } from "react-router-dom";
import {
  Mail,
  MapPin,
  Shield,
  FileText,
  AlertCircle,
  Cookie,
} from "lucide-react";
import { motion } from "framer-motion";
import favicon from "../assets/favicon.webp";
import "../styles/Footer.css";

const Footer = () => {
  const year = new Date().getFullYear();

  return (
    <footer className="footer select-none">
      <motion.div 
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="max-w-7xl mx-auto px-6 py-12 md:py-20"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 md:gap-16">
          {/* AESTHETIC BRANDING */}
          <div className="footer-brand space-y-8">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-primary-600/10 rounded-2xl flex items-center justify-center p-2.5 shadow-inner shrink-0">
                <img src={favicon} alt="Logo" className="w-full h-full object-contain" />
              </div>
              <h2 className="text-2xl font-black text-main uppercase tracking-tight leading-tight">
                Naturextrakte <span className="text-primary-600">OBFS</span>
              </h2>
            </div>
            <p className="text-sm font-medium text-muted leading-loose opacity-80 max-w-xs">
              Pioneering data-driven agronomy to help Indian farmers optimize yield, reduce costs, and preserve soil health for future generations.
            </p>
            <div className="flex flex-col gap-1">
               <span className="text-[10px] font-black uppercase text-primary-600/50 tracking-widest leading-none mb-1">Our Mission</span>
               <div className="h-[2px] w-12 bg-primary-600 rounded-full" />
               <p className="text-xs font-bold text-muted uppercase tracking-widest opacity-40">Sustainable. Profitable. Simple.</p>
            </div>
          </div>

          {/* EXPLORE SECTION */}
          <div className="footer-column space-y-8">
            <h3 className="footer-heading pb-2 border-b border-border-main/50 w-fit">Resources</h3>
            <ul className="footer-links flex flex-col gap-5">
              <li><Link to="/about" className="footer-link-item group"><div className="footer-icon-box"><FileText size={14}/></div>About Mission</Link></li>
              <li><Link to="/blogs" className="footer-link-item group"><div className="footer-icon-box"><Mail size={14}/></div>Farmer Insights</Link></li>
              <li><Link to="/feed" className="footer-link-item group"><div className="footer-icon-box"><Shield size={14}/></div>Community Feed</Link></li>
            </ul>
          </div>

          {/* LEGAL SECTION */}
          <div className="footer-column space-y-8">
            <h3 className="footer-heading pb-2 border-b border-border-main/50 w-fit">Compliance</h3>
            <ul className="footer-links flex flex-col gap-5">
              <li><Link to="/privacy-policy" className="footer-link-item"><div className="footer-icon-box"><Shield size={14}/></div>Privacy</Link></li>
              <li><Link to="/terms" className="footer-link-item"><div className="footer-icon-box"><FileText size={14}/></div>Terms</Link></li>
              <li><Link to="/disclaimer" className="footer-link-item"><div className="footer-icon-box"><AlertCircle size={14}/></div>Disclaimer</Link></li>
              <li><Link to="/cookie-policy" className="footer-link-item"><div className="footer-icon-box"><Cookie size={14}/></div>Cookies</Link></li>
            </ul>
          </div>

          {/* CONTACT & SUPPORT */}
          <div className="footer-column space-y-8">
            <h3 className="footer-heading pb-2 border-b border-border-main/50 w-fit">Connect</h3>
            <div className="flex flex-col gap-6">
               <div className="space-y-4">
                  <div className="footer-link-item"><div className="footer-icon-box"><Mail size={14}/></div>support@obfs.in</div>
                  <div className="footer-link-item"><div className="footer-icon-box"><MapPin size={14}/></div>Maharashtra, India</div>
               </div>
               <Link to="/contact" className="btn-footer-primary">
                 <Mail size={18} /> Support Center
               </Link>
            </div>
          </div>
        </div>

        {/* REFINED BOTTOM BAR */}
        <div className="footer-bottom mt-24 pt-10 flex flex-col lg:flex-row justify-between items-center gap-10">
          <div className="flex flex-col items-center lg:items-start gap-4 text-center lg:text-left">
             <div className="flex flex-wrap items-center justify-center lg:justify-start gap-2 text-[10px] sm:text-[10px] font-black uppercase tracking-[0.2em] text-muted opacity-50 leading-relaxed">
               <span>© {year}</span> 
               <span className="text-main">Naturextrakte Research and Innovation LLP</span>
             </div>
             <div className="flex items-center gap-3 sm:gap-6 opacity-40">
                <div className="h-[1px] w-8 sm:w-12 bg-border-main hidden lg:block" />
                <span className="text-[9px] font-black uppercase tracking-widest text-muted">All rights specifically reserved.</span>
             </div>
          </div>

          <a 
            href="https://tejashande.vercel.app" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="flex items-center gap-3 group bg-white dark:bg-stone-900 px-6 py-3 rounded-2xl border border-border-main shadow-lg shadow-black/5 hover:border-primary-400 hover:scale-[1.02] transition-all cursor-pointer"
          >
            <span className="text-[10px] font-black uppercase text-muted tracking-widest opacity-60 group-hover:text-primary-600 transition-colors">Developed by</span>
            <span className="dev-credit-link text-xs">
              TEJAS HANDE
            </span>
          </a>
        </div>
      </motion.div>
    </footer>
  );
};

export default Footer;
